import { computed, ref } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { isEqual } from 'lodash-es'
import pMemoize, { pMemoizeClear } from 'p-memoize'
import ExpiryMap from 'expiry-map'
import type { RatingRootCause } from '@zendesk/zqa-services/reviews'
import { draftContextMatches, draftConversationMatches } from '@klausapp/state/draft-context'
import useTicketRouteParams from '@/composables/useTicketRouteParams'
import { createOrUpdateDraft, deleteDraft, type Draft, getDrafts } from '@/modules/conversations/api/drafts'
import type { CommentTag } from '@/modules/shared/TicketContent/types/feedback'
import { bus } from '@/utils/bus'
import { uploadUnsavedImages } from '@/modules/shared/CommentEditor/plugins/useUnsavedImages'

// Pure context comparators live in @klausapp/state so the Vue app and any
// future non-Vue consumer share one copy. Re-exported here so existing
// consumers keep their import path (@/composables/useReviewDraft).
export { draftContextMatches, draftConversationMatches }

const VERSION = 'v2'
export interface DraftRating {
  categoryId: number
  rating?: number
  cause?: string[]
  rootCause?: RatingRootCause
}

export interface ReviewDraftContent {
  ratings: DraftRating[]
  comment: string
  userId?: number | string
  scorecardId?: string
  commentTags?: CommentTag[]
}

export interface DraftContext {
  connectionId?: string
  conversationId?: string
  messageId?: string
}

export default createSharedComposable(() => {
  const { connectionId, conversationId, messageId } = useTicketRouteParams()
  const drafts = ref<Draft[]>([])

  const _memGetDrafts = pMemoize(getDrafts, {
    cache: new ExpiryMap(1000),
    cacheKey: JSON.stringify,
  })

  const fetchDrafts = async () => {
    const res = await _memGetDrafts()
    drafts.value = res.drafts
  }

  const _getCurrentDraftContext = (): DraftContext => ({
    connectionId: connectionId.value?.toString(),
    conversationId: conversationId.value,
    messageId: messageId.value || undefined,
  })

  const _isDraftInContext = (draft: Draft, context: DraftContext) => draftContextMatches(draft, context)

  const _getDraftContent = (context: DraftContext): ReviewDraftContent | undefined => {
    const draft = drafts.value.find((draft) => _isDraftInContext(draft, context))
    return draft?.content && JSON.parse(draft.content)
  }

  const currentDraftContent = computed<ReviewDraftContent | undefined>(() =>
    _getDraftContent(_getCurrentDraftContext()),
  )

  const setDraft = async (content: ReviewDraftContent, context = _getCurrentDraftContext()) => {
    if (!context.connectionId || !context.conversationId) return

    const draft = {
      connectionId: context.connectionId,
      conversationId: context.conversationId,
      messageId: context.messageId,
      content: JSON.stringify(content),
      version: VERSION,
    }

    const currentContent = _getDraftContent(context)
    if (currentContent && isEqual(currentContent, JSON.parse(draft.content))) return

    await createOrUpdateDraft(draft)
    pMemoizeClear(_memGetDrafts)
    await uploadUnsavedImages(content.comment)

    drafts.value = [...drafts.value.filter((d) => !_isDraftInContext(d, context)), draft]

    bus.$emit('change-review-draft', { isRemoved: false, draft: context })
  }

  const _getDeleteDraftRequest = (context: DraftContext, discarded: boolean) => {
    return {
      connectionId: context.connectionId,
      conversationId: context.conversationId,
      messageId: context.messageId,
      version: VERSION,
      discarded,
    }
  }

  const removingDraftKeys = new Set<string>()
  const _getDraftContextKey = (context: DraftContext) =>
    JSON.stringify([context.connectionId?.toString() ?? '', context.conversationId ?? '', context.messageId ?? ''])

  const removeDraft = async (discarded = true, context = _getCurrentDraftContext()) => {
    if (!context.connectionId || !context.conversationId) return

    if (!drafts.value.some((draft) => _isDraftInContext(draft, context))) return

    const draftContextKey = _getDraftContextKey(context)
    if (removingDraftKeys.has(draftContextKey)) return

    removingDraftKeys.add(draftContextKey)
    try {
      await deleteDraft(_getDeleteDraftRequest(context, discarded))
      pMemoizeClear(_memGetDrafts)

      drafts.value = drafts.value.filter((draft) => !_isDraftInContext(draft, context))

      bus.$emit('change-review-draft', { isRemoved: true, draft: context, discarded })
    } finally {
      removingDraftKeys.delete(draftContextKey)
    }
  }

  return {
    drafts,
    fetchDrafts,
    currentDraftContent,
    setDraft,
    removeDraft,
  }
})
